import { useEffect, useRef } from "react"

const CELL_SIZE = 10
const COLS = 20
const ROWS = 10
const UPDATE_DELAY = 120
const LIFE_COLOR_VAR = "--color-white"
const FADE_COLOR_VAR = "--color-black-alpha-10"

function smartRandomGrid() {
    const grid = Array.from({ length: ROWS }, () =>
        Array.from({ length: COLS }, () => 0)
    )

    const glider = [
        [0, 1],
        [1, 2],
        [2, 0], [2, 1], [2, 2]
    ]

    const numGliders = 3
    for (let i = 0; i < numGliders; i++) {
        const offsetX = Math.floor(Math.random() * (COLS - 3))
        const offsetY = Math.floor(Math.random() * (ROWS - 3))

        glider.forEach(([dy, dx]) => {
            grid[offsetY + dy][offsetX + dx] = 1
        })
    }

    return grid
}

function gridToString(grid: number[][]) {
    return grid.map(row => row.join("")).join("|")
}

function countNeighbors(grid: number[][], x: number, y: number) {
    let count = 0
    for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
            if (dx === 0 && dy === 0) continue
            const nx = x + dx
            const ny = y + dy
            if (nx >= 0 && nx < COLS && ny >= 0 && ny < ROWS) {
                count += grid[ny][nx]
            }
        }
    }
    return count
}

export function LifeGame() {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const gridRef = useRef<number[][] | null>(null)
    const prevGridRef = useRef<string>("")
    const lastUpdateRef = useRef<number>(0)

    useEffect(() => {
        // Frame loop and reset timer live inside the effect so they are not
        // unstable outer deps (react-hooks/exhaustive-deps).
        let rafId: number | null = null
        let resetTimer: number | null = null
        const rootStyles = getComputedStyle(document.documentElement)
        const lifeColor = rootStyles.getPropertyValue(LIFE_COLOR_VAR).trim()
        const fadeColor = rootStyles.getPropertyValue(FADE_COLOR_VAR).trim()

        const animate = (time: number) => {
            const canvas = canvasRef.current
            if (!canvas || !gridRef.current) return
            const ctx = canvas.getContext("2d")
            if (!ctx) return

            if (time - lastUpdateRef.current >= UPDATE_DELAY) {
                const newGrid = gridRef.current.map(arr => [...arr])
                for (let y = 0; y < ROWS; y++) {
                    for (let x = 0; x < COLS; x++) {
                        const n = countNeighbors(gridRef.current!, x, y)
                        if (gridRef.current![y][x]) {
                            newGrid[y][x] = n === 2 || n === 3 ? 1 : 0
                        } else {
                            newGrid[y][x] = n === 3 ? 1 : 0
                        }
                    }
                }

                const isDead = newGrid.flat().every(cell => cell === 0)
                const isStable = gridToString(gridRef.current) === gridToString(newGrid)

                gridRef.current = newGrid
                lastUpdateRef.current = time

                if ((isDead || isStable) && resetTimer == null) {
                    resetTimer = window.setTimeout(() => {
                        gridRef.current = smartRandomGrid()
                        prevGridRef.current = ""
                        resetTimer = null
                    }, 500)
                }
            }

            ctx.save()
            ctx.globalCompositeOperation = "destination-out"
            ctx.fillStyle = fadeColor
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            ctx.restore()

            for (let y = 0; y < ROWS; y++) {
                for (let x = 0; x < COLS; x++) {
                    if (gridRef.current[y][x]) {
                        ctx.fillStyle = lifeColor
                        ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE)
                    }
                }
            }

            ctx.strokeStyle = lifeColor
            ctx.lineWidth = 0.7
            ctx.globalAlpha = 0.14
            for (let x = 0; x <= COLS; x++) {
                ctx.beginPath()
                ctx.moveTo(x * CELL_SIZE, 0)
                ctx.lineTo(x * CELL_SIZE, ROWS * CELL_SIZE)
                ctx.stroke()
            }
            for (let y = 0; y <= ROWS; y++) {
                ctx.beginPath()
                ctx.moveTo(0, y * CELL_SIZE)
                ctx.lineTo(COLS * CELL_SIZE, y * CELL_SIZE)
                ctx.stroke()
            }
            ctx.globalAlpha = 1

            rafId = requestAnimationFrame(animate)
        }

        gridRef.current = smartRandomGrid()
        prevGridRef.current = ""
        lastUpdateRef.current = performance.now()
        rafId = requestAnimationFrame(animate)

        return () => {
            if (rafId != null) cancelAnimationFrame(rafId)
            if (resetTimer != null) clearTimeout(resetTimer)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            width={COLS * CELL_SIZE}
            height={ROWS * CELL_SIZE}
            style={{
                display: "block",
                margin: "0 auto",
                background: "transparent",
            }}
        />
    )
}
